/**
 * Certification Runner — Operator UI (Phase 0 Batch D).
 *
 * Consumes:
 *   GET  /api/certification/e2e/scenarios     — list of KNOWN scenarios
 *   GET  /api/certification/e2e/capabilities  — role-derived can-flags
 *   GET  /api/certification/e2e/runs          — tenant-scoped executions
 *   POST /api/certification/e2e/runs          — start a synthetic run
 *   GET  /api/certification/e2e/runs/{id}     — run detail + transcript
 *   POST /api/certification/e2e/runs/{id}/cancel — request cancellation
 *   GET  /api/certification/e2e/runs/{id}/report — redacted report (post-purge = 410)
 *
 * Design contract:
 *   - Never renders raw PHI. Redacted report auto-scrubbed by the backend.
 *   - Poll interval (600ms) tuned to catch progress on the ~1s chain.
 *   - RBAC gate: falls back to a friendly "not authorised" view if
 *     the capabilities endpoint reports no start / list access.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  Play,
  ArrowClockwise,
  CheckCircle,
  XCircle,
  Clock,
  Warning,
  StopCircle,
  Eye,
  ShieldCheck,
} from '@phosphor-icons/react';

const POLL_INTERVAL_MS = 600;
const RUN_LIST_LIMIT = 50;

const STATUS_BADGE = {
  queued: 'bg-slate-100 text-slate-700',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-amber-100 text-amber-900',
};

const STEP_STATUS_ICON = {
  passed: { Icon: CheckCircle, className: 'text-emerald-600' },
  failed: { Icon: XCircle, className: 'text-red-600' },
  skipped: { Icon: Warning, className: 'text-amber-500' },
  not_run: { Icon: Clock, className: 'text-slate-400' },
};

const humanizeError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length < 200) return detail;
  if (detail?.message) return detail.message;
  if (err?.response?.status === 403) return 'Not authorised for this action.';
  if (err?.response?.status === 404) return 'Not found in the current tenant.';
  return 'Action failed. Please retry.';
};

const CertificationRunnerPage = () => {
  const { user } = useAuth();
  const [capabilities, setCapabilities] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [notes, setNotes] = useState('');
  const [starting, setStarting] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [reportOpen, setReportOpen] = useState(null); // {report_json, execution_id, gone}
  const [refreshTick, setRefreshTick] = useState(0);

  const canStart = !!capabilities?.can?.start_run;
  const canList = !!capabilities?.can?.list_runs;
  const canRead = !!capabilities?.can?.read_reports;

  // Load capabilities + scenarios on mount.
  useEffect(() => {
    (async () => {
      try {
        const [{ data: caps }, { data: scen }] = await Promise.all([
          api.get('/certification/e2e/capabilities'),
          api.get('/certification/e2e/scenarios').catch(() => ({ data: { scenarios: [] } })),
        ]);
        setCapabilities(caps);
        setScenarios(scen.scenarios || []);
        if ((scen.scenarios || []).length > 0) {
          setSelectedScenario(scen.scenarios[0].scenario_key);
        }
      } catch (e) {
        toast.error(humanizeError(e));
      }
    })();
  }, []);

  const fetchRuns = useCallback(async () => {
    if (!canList) return;
    setLoadingRuns(true);
    try {
      const { data } = await api.get(`/certification/e2e/runs?limit=${RUN_LIST_LIMIT}`);
      setRuns(data.items || []);
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setLoadingRuns(false);
    }
  }, [canList]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns, refreshTick]);

  // Live-poll the selected run while it's not terminal.
  useEffect(() => {
    if (!selectedRun) return;
    if (['completed', 'failed', 'cancelled'].includes(selectedRun.status)) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/certification/e2e/runs/${selectedRun.id}`);
        setSelectedRun(data);
        // Also refresh the summary list so the badge on the row updates.
        setRuns((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      } catch (e) {
        // ignore transient
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedRun?.id, selectedRun?.status]);

  const startRun = async () => {
    if (!selectedScenario) {
      toast.error('Pick a scenario first.');
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post('/certification/e2e/runs', {
        scenario_key: selectedScenario,
        notes: notes.trim() || undefined,
      });
      toast.success(`Run queued (${data.id.slice(0, 12)}…)`);
      setNotes('');
      setSelectedRun(data);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setStarting(false);
    }
  };

  const cancelRun = async (runId) => {
    try {
      const { data } = await api.post(`/certification/e2e/runs/${runId}/cancel`, {
        reason: 'operator_cancelled_from_ui',
      });
      toast.success('Cancellation requested.');
      setSelectedRun(data);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(humanizeError(e));
    }
  };

  const viewReport = async (runId) => {
    try {
      const { data } = await api.get(`/certification/e2e/runs/${runId}/report`);
      setReportOpen({ execution_id: runId, report_json: data.report, gone: false });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        setReportOpen({ execution_id: runId, report_json: null, gone: true });
        return;
      }
      toast.error(humanizeError(e));
    }
  };

  const scenarioMeta = useMemo(
    () => scenarios.find((s) => s.scenario_key === selectedScenario) || null,
    [scenarios, selectedScenario],
  );

  if (!capabilities) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16 text-slate-500" data-testid="cert-runner-loading">
          Loading capabilities…
        </div>
      </Layout>
    );
  }

  if (!canList && !canStart) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 text-center" data-testid="cert-runner-forbidden">
          <Warning size={48} className="mx-auto text-amber-500 mb-4" weight="duotone" />
          <h1 className="text-xl font-semibold text-slate-900">
            Certification Runner is restricted
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Your role does not include access to the certification runner.
            Ask an admin for one of the <code>certification_operator</code>,{' '}
            <code>certification_reviewer</code>, <code>certification_auditor</code>,{' '}
            or <code>certification_admin</code> roles.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="certification-runner-page">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Certification Runner
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Synthetic ODM 270/271 E2E executions. Every run uses the versioned
              synthetic dataset — no real PHI, no live ODM traffic.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRefreshTick((t) => t + 1)}
              disabled={loadingRuns}
              data-testid="cert-runner-refresh-btn"
            >
              <ArrowClockwise size={16} className="mr-1.5" />
              Refresh
            </Button>
          </div>
        </header>

        {/* Start-run panel */}
        <div
          className="bg-white border border-slate-200 rounded-lg p-4 space-y-3"
          data-testid="cert-runner-start-panel"
        >
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-600" />
            <h2 className="text-sm font-semibold text-slate-800">Start a synthetic run</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-xs font-medium text-slate-600">Scenario</label>
              <Select
                value={selectedScenario}
                onValueChange={setSelectedScenario}
                disabled={!canStart || starting}
              >
                <SelectTrigger data-testid="cert-runner-scenario-select">
                  <SelectValue placeholder="Pick a scenario" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {scenarios.map((s) => (
                    <SelectItem key={s.scenario_key} value={s.scenario_key}>
                      {s.scenario_key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scenarioMeta && (
                <p className="mt-1 text-xs text-slate-500" data-testid="cert-runner-scenario-desc">
                  {scenarioMeta.description}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600">
                Notes (optional, ≤ 500 chars)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                placeholder="Context for this run (e.g. re-verifying after fixture bump)…"
                disabled={!canStart || starting}
                data-testid="cert-runner-notes"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={startRun}
              disabled={!canStart || starting || !selectedScenario}
              data-testid="cert-runner-start-btn"
            >
              <Play size={16} className="mr-1.5" />
              {starting ? 'Starting…' : 'Start run'}
            </Button>
          </div>
        </div>

        {/* Runs table */}
        <div
          className="bg-white border border-slate-200 rounded-lg overflow-hidden"
          data-testid="cert-runner-runs-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Scenario</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Progress</th>
                <th className="text-left px-4 py-2">Reconciliation</th>
                <th className="text-left px-4 py-2">Started</th>
                <th className="text-left px-4 py-2">Duration</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.length === 0 && !loadingRuns && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="cert-runner-empty">
                    No runs yet. Start one above.
                  </td>
                </tr>
              )}
              {loadingRuns && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    Loading runs…
                  </td>
                </tr>
              )}
              {runs.map((r) => {
                const dur = r.started_at && r.completed_at
                  ? `${(
                      (new Date(r.completed_at).getTime() -
                        new Date(r.started_at).getTime()) /
                      1000
                    ).toFixed(2)}s`
                  : '—';
                return (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedRun(r)}
                    data-testid={`cert-runner-row-${r.id}`}
                  >
                    <td className="px-4 py-2 font-mono text-xs">{r.scenario_key}</td>
                    <td className="px-4 py-2">
                      <Badge className={STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700'}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <ProgressBar current={r.progress?.current_step || 0} total={r.progress?.total_steps || 14} />
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {r.reconciliation_outcome || '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {formatDateTime(r.started_at || r.created_at) || '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{dur}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRun(r);
                          }}
                          data-testid={`cert-runner-view-${r.id}`}
                        >
                          <Eye size={14} className="mr-1" />
                          View
                        </Button>
                        {canRead && r.redacted_report_available && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              viewReport(r.id);
                            }}
                            data-testid={`cert-runner-report-${r.id}`}
                          >
                            Report
                          </Button>
                        )}
                        {['queued', 'running'].includes(r.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelRun(r.id);
                            }}
                            data-testid={`cert-runner-cancel-${r.id}`}
                          >
                            <StopCircle size={14} className="mr-1" />
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RunDetailDrawer
        run={selectedRun}
        onClose={() => setSelectedRun(null)}
        onCancel={() => cancelRun(selectedRun.id)}
        onViewReport={() => viewReport(selectedRun.id)}
        canRead={canRead}
      />

      <ReportDialog reportInfo={reportOpen} onClose={() => setReportOpen(null)} />
    </Layout>
  );
};

const ProgressBar = ({ current, total }) => {
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full bg-blue-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-slate-500 font-mono">
        {current}/{total}
      </span>
    </div>
  );
};

const StepIcon = ({ status }) => {
  const spec = STEP_STATUS_ICON[status] || STEP_STATUS_ICON.not_run;
  const { Icon, className } = spec;
  return <Icon size={16} className={className} weight="fill" />;
};

const RunDetailDrawer = ({ run, onClose, onCancel, onViewReport, canRead }) => {
  if (!run) return null;
  return (
    <Sheet open={!!run} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="cert-runner-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="font-mono text-sm">{run.scenario_key}</span>
            <Badge className={STATUS_BADGE[run.status] || 'bg-slate-100 text-slate-700'}>
              {run.status}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            Redacted metadata only — no PHI. Transcript updates in near-real-time.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <MetaBlock label="Execution id" value={run.id} mono />
            <MetaBlock label="Agency id" value={run.agency_id} mono />
            <MetaBlock label="Started" value={formatDateTime(run.started_at) || '—'} />
            <MetaBlock label="Completed" value={formatDateTime(run.completed_at) || '—'} />
            <MetaBlock label="Reconciliation" value={run.reconciliation_outcome || '—'} />
            <MetaBlock label="Report available" value={run.redacted_report_available ? 'yes' : 'no'} />
          </div>

          {run.notes && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Notes</p>
              <p className="text-sm text-slate-700 whitespace-pre-line">{run.notes}</p>
            </div>
          )}

          {run.summary?.counts && (
            <div className="flex flex-wrap gap-2" data-testid="cert-runner-summary-counts">
              {['passed', 'failed', 'skipped', 'not_run'].map((k) => (
                <Badge
                  key={k}
                  className={
                    k === 'failed' && run.summary.counts[k] > 0
                      ? 'bg-red-100 text-red-800'
                      : k === 'passed'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-700'
                  }
                >
                  {k}: {run.summary.counts[k] || 0}
                </Badge>
              ))}
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Step transcript</p>
            <ol className="space-y-1 border border-slate-200 rounded-md p-2 bg-slate-50">
              {(run.transcript || []).map((s) => (
                <li key={s.step_id} className="flex items-start gap-2 text-xs">
                  <StepIcon status={s.status} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-slate-700">{s.step_id}</span>
                      <span className="text-slate-400">{s.duration_ms}ms</span>
                    </div>
                    <p className="text-slate-500 mt-0.5">{s.detail}</p>
                    {Array.isArray(s.leaks_detected) && s.leaks_detected.length > 0 && (
                      <p className="text-red-600 mt-0.5">
                        leaks_detected: {s.leaks_detected.length}
                      </p>
                    )}
                  </div>
                </li>
              ))}
              {(!run.transcript || run.transcript.length === 0) && (
                <li className="text-xs text-slate-400 text-center py-2">
                  Waiting for first step…
                </li>
              )}
            </ol>
          </div>

          <div className="flex items-center gap-2">
            {['queued', 'running'].includes(run.status) && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-700"
                onClick={onCancel}
                data-testid="cert-runner-drawer-cancel"
              >
                <StopCircle size={14} className="mr-1" />
                Cancel run
              </Button>
            )}
            {canRead && run.redacted_report_available && (
              <Button
                size="sm"
                onClick={onViewReport}
                data-testid="cert-runner-drawer-view-report"
              >
                <Eye size={14} className="mr-1" />
                View redacted report
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const ReportDialog = ({ reportInfo, onClose }) => {
  if (!reportInfo) return null;
  return (
    <Sheet open={!!reportInfo} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="cert-runner-report-drawer">
        <SheetHeader>
          <SheetTitle>Redacted Operator Report</SheetTitle>
          <SheetDescription>
            {reportInfo.execution_id}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4">
          {reportInfo.gone ? (
            <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="cert-runner-report-gone">
              This report was purged by Step 11 (per synthetic hygiene
              policy). The audit trail still records that the report existed
              and who accessed it.
            </div>
          ) : (
            <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto max-h-[70vh]" data-testid="cert-runner-report-json">
              {JSON.stringify(reportInfo.report_json, null, 2)}
            </pre>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MetaBlock = ({ label, value, mono }) => (
  <div>
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className={`text-sm text-slate-800 ${mono ? 'font-mono break-all' : ''}`}>
      {value ?? '—'}
    </p>
  </div>
);

export default CertificationRunnerPage;
